import React, { useState } from "react";
import EventToggleIcon from "./EventToggleIcon";
import { EventsWeekData } from "../../types";
interface EventsWeekCardProps {
  data: EventsWeekData;
  isStaticDisabled?: boolean;
}

const parseEventTime = (time: string): { value: string; period: string } => {
  const match = time.trim().match(/^(.+?)\s+(AM|PM)$/i);
  if (match) {
    return { value: match[1], period: match[2].toUpperCase() };
  }
  return { value: time, period: "" };
};

const EventsWeekCard: React.FC<EventsWeekCardProps> = ({
  data,
  isStaticDisabled = false,
}) => {
  const [activeMonthIndex, setActiveMonthIndex] = useState(data.activeMonthIndex);
  const [toggleStates, setToggleStates] = useState<Record<string, boolean>>(() =>
    data.events.reduce<Record<string, boolean>>((acc, event) => {
      if (event.toggleEnabled !== undefined) {
        acc[event.id] = event.toggleEnabled;
      }
      return acc;
    }, {})
  );

  return (
    <div
      className={`events-week-card${
        isStaticDisabled ? " events-week-card--static-disabled" : ""
      }`}
    >
      <h3 className="events-week-card__title">Events / This Week</h3>

      <div className="events-week-card__months">
        {data.months.map((month, index) => (
          <button
            key={month}
            type="button"
            className={`events-week-card__month ${
              activeMonthIndex === index ? "events-week-card__month--active" : ""
            }`}
            onClick={() => {
              if (!isStaticDisabled) {
                setActiveMonthIndex(index);
              }
            }}
            disabled={isStaticDisabled}
          >
            {month}
          </button>
        ))}
      </div>

      <div className="events-week-card__week">
        {data.weekDays.map((day) => (
          <div key={`${day.label}-${day.date}`} className="events-week-card__day">
            <span className="events-week-card__day-label">{day.label}</span>
            <span
              className={`events-week-card__day-date ${
                day.isToday ? "events-week-card__day-date--today" : ""
              }`}
            >
              {day.date}
            </span>
          </div>
        ))}
      </div>

      <div className="events-week-card__schedule">
        {data.events.length > 0 ? (
          data.events.map((event) => {
          const { value: timeValue, period: timePeriod } = parseEventTime(event.time);

          return (
          <div
            key={event.id}
            className={`events-week-card__event events-week-card__event--${event.variant}`}
          >
            <div className="events-week-card__event-time">
              <span className="events-week-card__event-time-value">{timeValue}</span>
              {timePeriod && (
                <span className="events-week-card__event-time-period">{timePeriod}</span>
              )}
            </div>
            <span className="events-week-card__event-divider" aria-hidden />
            <div className="events-week-card__event-content">
              <span className="events-week-card__event-title">{event.title}</span>
              {event.subtitle && (
                <span className="events-week-card__event-subtitle">{event.subtitle}</span>
              )}
            </div>
            {event.toggleEnabled !== undefined && (
              <button
                type="button"
                className="events-week-card__toggle"
                onClick={() =>
                  setToggleStates((prev) => ({
                    ...prev,
                    [event.id]: !prev[event.id],
                  }))
                }
                aria-label={`Toggle ${event.title}`}
                aria-pressed={toggleStates[event.id]}
                disabled={isStaticDisabled}
              >
                <EventToggleIcon isOn={toggleStates[event.id]} />
              </button>
            )}
          </div>
          );
        })
        ) : (
          <div className="events-week-card__empty">No events scheduled</div>
        )}
      </div>
    </div>
  );
};

export default EventsWeekCard;
